import React, {lazy, useRef, useState} from "react";
import {useScrollPosition} from '@n8tb1t/use-scroll-position';
import styles from "./PreparedAdStructure.module.scss";
import {SizeMe} from "react-sizeme";
import {IProduct} from 'data/interfaces/products';
import {IOrder, IOrderOptions} from 'data/interfaces/orders';
import {ITransaction} from 'data/interfaces/transactions';
import {IUser} from 'data/redux/reducers/authenticationReducer';
import AdRegisterOrLogin from '../adRegisterOrLogin';
import PurchaseForm from "./components/purchaseForm/PurchaseForm";
import {Case, Default, Else, If, Switch, Then, When} from 'react-if';
import {PRODUCT_STATUS_PUBLISHED} from 'data/redux/constants/productsConstants';
import SaleConclusion from './components/saleConclusion/SaleConclusion';
import AdStructure from '../adStructure';

import PurchaseButton from "../conclusionCard/components/purchaseButton/PurchaseButton";
import PaymentOptions
  from 'gui/pages/mainRouter/pages/ad/components/commons/preparedAdStructure/components/purchaseForm/components/guarantees/components/paymentOptions';
import GuaranteePoints
  from 'gui/pages/mainRouter/pages/ad/components/commons/preparedAdStructure/components/purchaseForm/components/guarantees/components/guaranteePoints';
import MobileChatButton from "gui/components/crispChat/components/mobileChatButton/MobileChatButton";
import {getPageHeight} from 'data/utils/calculators';

const SecondStage = lazy(() => import(/* webpackChunkName: "SecondStage" , webpackMode:'lazy' */ '../secondStage'));
const ThirdStage = lazy(() => import(/* webpackChunkName: "ThirdStage" , webpackMode:'lazy' */ '../thirdStage'));


export interface IPreparedAdStructureProps {
  product: IProduct;
  order: IOrder;
  transaction?: ITransaction;
  user: IUser;
  signedIn?: boolean;
  isAdmin?: boolean;
  isProductOwner?: boolean;
  isOrderOwner?: boolean;
  beginOrder?: (product: IProduct, options: IOrderOptions) => Promise<void>;

  title: string;
  firstStageComponent?: any;
  originalPrice: number;
  saleDetails?: any;
  purchaseDetails?: any;
  purchaseDetailsFields?: any;
  purchaseDetailsFormInitialValue?: any;
  aboveConclusionCardComponent?: any;
  loadProduct?: () => Promise<any>;
}

const PreparedAdStructure = (props: IPreparedAdStructureProps) => {
  const registerFormRef: any = useRef();
  const [sticky, setSticky] = useState(true);
  const [scrollY, setScrollY] = useState(0);

  const orderExist = !!(props.order && props.order.id);
  const passengers = (props.order && props.order.options && props.order.options.passengers) || [];
  const isClickedPurchase = props.isProductOwner !== true && (passengers.length > 0 || orderExist);
  useScrollPosition(({prevPos, currPos}) => {
    const scrollHeight = getPageHeight() - Math.abs(currPos.y);
    if (scrollHeight > 811 && scrollHeight > scrollY) {
      setSticky(true);
    } else {
      setSticky(false);
    }
    setScrollY(scrollHeight);
  });
  const purchaseButtonClick = async (formValues: any = {}) => {
    const passengers = [];
    if (formValues.ticketsAmount) {
      for (let i = 0; i < formValues.ticketsAmount; i++) {
        if (isClickedPurchase && props.order.options.passengers[i]) {
          passengers.push({
            firstName: props.order.options.passengers[i].firstName,
            lastName: props.order.options.passengers[i].lastName,
            birthDate: props.order.options.passengers[i].birthDate,
          });
        } else {
          passengers.push({
            firstName: "",
            lastName: "",
            birthDate: null,
          });
        }
      }
    } else {
      passengers.push({
        firstName: "",
        lastName: "",
        birthDate: 9999999999999,
      });
    }

    await props.beginOrder(props.product, {passengers});
    if (!props.signedIn && registerFormRef && registerFormRef.current) {
      registerFormRef.current.scrollIntoView()
      window.scrollBy({
        left: 0, top: -55, behavior: "smooth"
      });
    }
    return Promise.resolve();
  };
  return (
    <SizeMe>{({size: {width}}) => {
      return (
        <AdStructure product={props.product}
                     order={props.order}
                     isAdmin={props.isAdmin}
                     isProductOwner={props.isProductOwner}
                     title={props.title}
                     cards={<Switch>
                       <Case condition={orderExist}>
                         {() => <ThirdStage {...props}
                                            bottomChildren={<props.firstStageComponent product={props.product}/>}/>}
                       </Case>
                       <Case condition={!!(props.signedIn && isClickedPurchase)}>
                         {() => <SecondStage beginOrder={props.beginOrder}
                                             order={props.order}
                                             product={props.product} user={props.user}
                                             isAdmin={props.isAdmin}
                         />}
                       </Case>
                       <Default>
                         {() => <props.firstStageComponent product={props.product}/>}
                       </Default>
                     </Switch>}
                     rightSideCard={(
                       <If condition={props.product.status !== PRODUCT_STATUS_PUBLISHED}>
                         <Then>{() => <SaleConclusion
                           aboveComponent={props.aboveConclusionCardComponent}
                           salePrice={props.product.saleDetails.askingPrice}
                           currency={props.product.saleDetails.currency}
                           originalPrice={props.originalPrice}
                           product={props.product}
                           order={props.order}
                           isProductOwner={props.isProductOwner}
                           isOrderOwner={props.isOrderOwner}
                           details={props.saleDetails}
                           loadProduct={props.loadProduct}
                         />}
                         </Then>
                         <Else>{() => <PurchaseForm aboveComponent={props.aboveConclusionCardComponent}
                                                    salePrice={props.product.saleDetails.askingPrice}
                                                    currency={props.product.saleDetails.currency}
                                                    originalPrice={props.originalPrice}
                                                    isProductOwner={props.isProductOwner}
                                                    onSubmit={purchaseButtonClick}
                                                    hidePurchaseButton={window.innerWidth < 768 || (props.signedIn && isClickedPurchase)}
                                                    details={props.purchaseDetails}
                                                    top={props.purchaseDetailsFields}
                                                    initialValues={props.purchaseDetailsFormInitialValue}

                         />}
                         </Else>
                       </If>
                     )}
                     bottomCard={(
                       <>
                         <When
                           condition={!isClickedPurchase && props.product.status === PRODUCT_STATUS_PUBLISHED && window.innerWidth < 768}>
                           {() =>
                             <>
                               <GuaranteePoints className={styles.guaranteePoints}/>
                               <PaymentOptions/>
                               <div
                                 className={`${styles.mobilePurchaseButton} ${sticky ? styles.mobileStickyPlaceHolderShow : styles.mobileStickyPlaceHolderHide}`.trim()}/>
                               <div
                                 className={`${styles.mobilePurchaseButton} ${sticky ? styles.mobileSticky : ''}`.trim()}>
                                 <PurchaseButton
                                   className={styles.purchaseButton}
                                   submitForm={() => purchaseButtonClick(props.purchaseDetailsFormInitialValue)}
                                 />
                                 <MobileChatButton/>
                               </div>
                             </>
                           }
                         </When>

                         <div
                           ref={registerFormRef}
                         >
                           <AdRegisterOrLogin
                             signedIn={props.signedIn}
                             clickedPurchase={isClickedPurchase}
                           />
                           <div className={styles.bottomMargin}/>
                         </div>
                       </>
                     )}
                     isClickedPurchase={isClickedPurchase}
        />
      );
    }
    }</SizeMe>
  );
};

PreparedAdStructure.defaultProps = {
  product: {},
  isProductOwner: false,
  isOrderOwner: false,
};

export default PreparedAdStructure;
